// ============================================
// חלק 1/5: Imports + Interfaces + State
// מחשבון M1 חשמלי - ייבוא מסחרי
// ============================================

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { printCalculationDocument } from "../../../utils/printCalculationDocument";
import {
  getCalculatorData,
  calculateN1N2ElectricCommercial,
  calculateN1N2ElectricCommercialFinal,
  saveCalculationWithNotes,
  CurrencyRate,
  ExplanationData,
} from "../../../services/enduser/calculatorService";
import MotionCard, { MotionButton } from "../../shared/MotionCard";
import ExplanationTooltip from "../../shared/ExplanationTooltip";
import TaxYearNotice from "../../shared/TaxYearNotice";
import { formatThousands, toRawNumber } from "../../../utils/numberInput";
import ActualCostCalculator, { ActualCostData } from "../../shared/ActualCostCalculator";

// ============================================
// ממשקים (Interfaces)
// ============================================

// ממשק נתוני טופס - M1 חשמלי מסחרי
interface FormData {
  manufacturing_date: string;
  vehicle_price_foreign: string;
  currency_type: string;
  exchange_rate: string;
  shipping_cost_foreign: string;
  insurance_cost_foreign: string;
  insurance_currency_type: string;
  insurance_exchange_rate: string;
  local_expenses_ils: string;
  has_origin_declaration: boolean;
  vehicle_weight_category:
    | "under_3500"
    | "between_3500_4500"
    | "over_4500"
    | "";
  airbags_count: string; // למסלול 2 בלבד
}

// ממשק אבזור בטיחותי M1 - עדכון נובמבר 2025
interface SafetyEquipmentData {
  airbagsCount: number;
  systems: {
    blindSpotDetection: boolean; // 1 - זיהוי כלי רכב ב"שטח מת"
    blindSpotCollisionPrevention: boolean; // 2 - אקטיבית למניעת התנגשות צד
    reverseBraking: boolean; // 3 - בלימה אוטומטית לאחור
    driverDistraction: boolean; // 4 - התרעה היסח דעת הנהג
    childAlert: boolean; // 5 - התרעה ילדים ברכב
    doorOpenWarning: boolean; // 6 - אזהרת פתיחת דלת
    adaptiveLighting: boolean; // 7 - תאורה אדפטיבית
    junctionEmergencyBraking: boolean; // 8 - בלימת חירום בצמתים
  };
}

// ============================================
// קבועים
// ============================================

// ציוני מערכות בטיחות - עדכון נובמבר 2025
const SAFETY_SYSTEMS_SCORES = {
  blindSpotDetection: 1.0,
  blindSpotCollisionPrevention: 2.0,
  reverseBraking: 1.0,
  driverDistraction: 1.0,
  childAlert: 1.5,
  doorOpenWarning: 1.5,
  adaptiveLighting: 1.0,
  junctionEmergencyBraking: 1.5,
} as const;

// רמות אבזור והנחות
const SAFETY_LEVELS = [
  { level: 0, minScore: 0, minAirbags: 0, discount: 0 },
  { level: 1, minScore: 1, minAirbags: 4, discount: 250 },
  { level: 2, minScore: 2, minAirbags: 4, discount: 600 },
  { level: 3, minScore: 3, minAirbags: 4, discount: 1000 },
  { level: 4, minScore: 4, minAirbags: 4, discount: 1600 },
  { level: 5, minScore: 5, minAirbags: 4, discount: 1850 },
  { level: 6, minScore: 6, minAirbags: 4, discount: 2050 },
  { level: 7, minScore: 8, minAirbags: 4, discount: 2250 },
  { level: 8, minScore: 10, minAirbags: 4, discount: 2400 },
] as const;

// מפתחות הסברים
const STEP_KEYS = {
  manufacturing_date: "manufacturing_date",
  vehicle_price: "vehicle_price",
  currency_selection: "currency_selection",
  exchange_rate: "exchange_rate",
  shipping_cost: "shipping_cost",
  insurance_cost: "insurance_cost",
  insurance_currency: "insurance_currency",
  insurance_exchange_rate: "insurance_exchange_rate",
  local_expenses: "local_expenses",
  origin_declaration: "origin_declaration",
  weight_category: "weight_category",
  airbags_count: "airbags_count",
  green_rating: "green_rating",
  // vehicle_weight: "vehicle_weight",
  consumer_price: "consumer_price_commercial", // ✅ שונה!
} as const;

const SAFETY_LEVEL_TO_FEATURES: Record<number, { airbagsCount: number; systems: Record<string, boolean> }> = {
  0: { airbagsCount: 0, systems: { blindSpotDetection: false, blindSpotCollisionPrevention: false, reverseBraking: false, driverDistraction: false, childAlert: false, doorOpenWarning: false, adaptiveLighting: false, junctionEmergencyBraking: false } },
  1: { airbagsCount: 4, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: false, driverDistraction: false, childAlert: false, doorOpenWarning: false, adaptiveLighting: false, junctionEmergencyBraking: false } },
  2: { airbagsCount: 4, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: true,  driverDistraction: false, childAlert: false, doorOpenWarning: false, adaptiveLighting: false, junctionEmergencyBraking: false } },
  3: { airbagsCount: 4, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: true,  driverDistraction: true,  childAlert: false, doorOpenWarning: false, adaptiveLighting: false, junctionEmergencyBraking: false } },
  4: { airbagsCount: 4, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: true,  driverDistraction: true,  childAlert: false, doorOpenWarning: false, adaptiveLighting: true,  junctionEmergencyBraking: false } },
  5: { airbagsCount: 4, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: true,  driverDistraction: true,  childAlert: true,  doorOpenWarning: false, adaptiveLighting: true,  junctionEmergencyBraking: false } },
  6: { airbagsCount: 4, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: true,  driverDistraction: true,  childAlert: true,  doorOpenWarning: true,  adaptiveLighting: true,  junctionEmergencyBraking: false } },
  7: { airbagsCount: 4, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: false, reverseBraking: true,  driverDistraction: true,  childAlert: true,  doorOpenWarning: true,  adaptiveLighting: true,  junctionEmergencyBraking: true  } },
  8: { airbagsCount: 4, systems: { blindSpotDetection: true,  blindSpotCollisionPrevention: true,  reverseBraking: true,  driverDistraction: true,  childAlert: true,  doorOpenWarning: true,  adaptiveLighting: true,  junctionEmergencyBraking: true  } },
};

const SAFETY_LEVEL_DISCOUNTS: Record<number, number> = {
  0: 0, 1: 250, 2: 600, 3: 1000, 4: 1600, 5: 1850, 6: 2050, 7: 2250, 8: 2400,
};

// ============================================
// קומפוננטה ראשית
// ============================================

const N1N2ElectricCommercialCalculatorPage: React.FC = () => {
  const navigate = useNavigate();

  // ========================================
  // State Management
  // ========================================

  // שלב נוכחי: 1 = קלט, 2 = מחיר לצרכן (רק מסלול 3), 3 = תוצאות
  const [currentStep, setCurrentStep] = useState<number>(1);

  // נתוני הטופס
  const [formData, setFormData] = useState<FormData>({
    manufacturing_date: "",
    vehicle_price_foreign: "",
    currency_type: "EUR",
    exchange_rate: "",
    shipping_cost_foreign: "",
    insurance_cost_foreign: "",
    insurance_currency_type: "EUR",
    insurance_exchange_rate: "",
    local_expenses_ils: "",
    has_origin_declaration: false,
    vehicle_weight_category: "",
    airbags_count: "",
  });

  // אבזור בטיחותי (רק למסלול 3 - מתחת ל-3.5 טון)
  const [safetyEquipmentData, setSafetyEquipmentData] =
    useState<SafetyEquipmentData>({
      airbagsCount: 0,
      systems: {
        blindSpotDetection: false,
        blindSpotCollisionPrevention: false,
        reverseBraking: false,
        driverDistraction: false,
        childAlert: false,
        doorOpenWarning: false,
        adaptiveLighting: false,
        junctionEmergencyBraking: false,
      },
    });

  // תוצאות חישוב בסיסי (שלב 1)
  const [basicCalculationResult, setBasicCalculationResult] =
    useState<any>(null);

  // מחיר לצרכן (שלב 2 - רק למסלול 3)
  const [consumerPrice, setConsumerPrice] = useState<string>("");

  // תוצאות סופיות (שלב 3)
  const [finalCalculationResult, setFinalCalculationResult] =
    useState<any>(null);

  // ref לאזור התוצאה — מקור לשכפול בהדפסה החדשה (גרסת פיתוח)
  const printRef = useRef<HTMLDivElement>(null);

  // נתוני עזר מהשרת
  const [currencyRates, setCurrencyRates] = useState<CurrencyRate[]>([]);
  const [explanations, setExplanations] = useState<
    Record<string, ExplanationData>
  >({});

  // מצב UI
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // שמירה
  const [userNotes, setUserNotes] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [commercialActualCostData, setCommercialActualCostData] = useState<ActualCostData | null>(null);
  const [safetyInputMode, setSafetyInputMode] = useState<"select" | "calculator">("select");
  const [selectedSafetyLevel, setSelectedSafetyLevel] = useState<number>(0);

  // ========================================
  // Computed Values (useMemo)
  // ========================================

  // חישוב ציון מערכות בטיחות (realtime)
  const safetyScore = useMemo(() => {
    return Object.entries(safetyEquipmentData.systems || {})
      .filter(([_, enabled]) => enabled)
      .reduce((sum, [systemKey, _]) => {
        return (
          sum +
          (SAFETY_SYSTEMS_SCORES[
            systemKey as keyof typeof SAFETY_SYSTEMS_SCORES
          ] || 0)
        );
      }, 0);
  }, [safetyEquipmentData.systems]);

  // חישוב רמת אבזור והנחה (realtime) - מודע למצב select/calculator
  const safetyLevel = useMemo(() => {
    if (safetyInputMode === "select") {
      return selectedSafetyLevel;
    }
    // מצב מחשבון — חישוב מהצ'קבוקסים
    const eligibleLevels = SAFETY_LEVELS.filter(
      (level) =>
        safetyScore >= level.minScore &&
        safetyEquipmentData.airbagsCount >= level.minAirbags
    );
    return eligibleLevels.length > 0 ? eligibleLevels[eligibleLevels.length - 1].level : 0;
  }, [safetyInputMode, selectedSafetyLevel, safetyScore, safetyEquipmentData.airbagsCount]);

  // הנחת אבזור בטיחותי
  const safetyDiscount = useMemo(() => {
    return SAFETY_LEVEL_DISCOUNTS[safetyLevel] || 0;
  }, [safetyLevel]);

  // האם מסלול 3 (צריך שלב 2)
  const needsStep2 = useMemo(() => {
    return formData.vehicle_weight_category === "under_3500";
  }, [formData.vehicle_weight_category]);

  // ========================================
  // useEffect Hooks
  // ========================================

  // טעינת נתוני עזר בטעינה ראשונית
  useEffect(() => {
    loadCalculatorData();
  }, []);

  const loadCalculatorData = async () => {
    try {
      const response = await getCalculatorData();
      if (response.res === "OK" && response.data) {
        setCurrencyRates(response.data.currency_rates || []);
        setExplanations(response.data.explanations || {});
      }
    } catch (error) {
      console.error("שגיאה בטעינת נתוני המחשבון:", error);
      setError("שגיאה בטעינת נתוני המחשבון");
    }
  };

  // עדכון אוטומטי של שער חליפין כשמשנים מטבע
  useEffect(() => {
    if (formData.currency_type && currencyRates.length > 0) {
      const selectedCurrency = currencyRates.find(
        (rate) => rate.currency_code === formData.currency_type
      );
      if (selectedCurrency) {
        setFormData((prev) => ({
          ...prev,
          exchange_rate: Number(selectedCurrency.customs_rate).toFixed(4),
        }));
      }
    }
  }, [formData.currency_type, currencyRates]);

  // עדכון אוטומטי של שער חליפין ביטוח
  useEffect(() => {
    if (formData.insurance_currency_type && currencyRates.length > 0) {
      const selectedCurrency = currencyRates.find(
        (rate) => rate.currency_code === formData.insurance_currency_type
      );
      if (selectedCurrency) {
        setFormData((prev) => ({
          ...prev,
          insurance_exchange_rate: Number(
            selectedCurrency.customs_rate
          ).toFixed(4),
        }));
      }
    }
  }, [formData.insurance_currency_type, currencyRates]);

  // ========================================
  // Helper Functions
  // ========================================

  // מניעת אפסים מיותרים בתחילת מספר
  const handleNumberInputChange = (field: keyof FormData, value: string) => {
    const cleanValue = value.replace(/^0+(?=\d)/, "");
    setFormData((prev) => ({ ...prev, [field]: cleanValue }));
  };

  // עדכון checkbox של אבזור בטיחותי
  const handleSafetySystemChange = (
    systemKey: keyof SafetyEquipmentData["systems"]
  ) => {
    setSafetyEquipmentData((prev) => ({
      ...prev,
      systems: {
        ...prev.systems,
        [systemKey]: !prev.systems[systemKey],
      },
    }));
  };

  // עדכון מספר כריות אוויר
  const handleAirbagsCountChange = (value: string) => {
    const numValue = parseInt(value) || 0;
    setSafetyEquipmentData((prev) => ({
      ...prev,
      airbagsCount: numValue,
    }));
  };

  // ========================================
  // Validation Functions
  // ========================================

  const validateStep1 = (): boolean => {
    // בדיקות בסיסיות
    if (
      !formData.vehicle_price_foreign ||
      parseFloat(formData.vehicle_price_foreign) <= 0
    ) {
      setError("נא להזין מחיר רכב תקין");
      return false;
    }

    if (!formData.currency_type) {
      setError("נא לבחור סוג מטבע");
      return false;
    }

    if (!formData.exchange_rate || parseFloat(formData.exchange_rate) <= 0) {
      setError("נא להזין שער חליפין תקין");
      return false;
    }

    if (
      !formData.shipping_cost_foreign ||
      parseFloat(formData.shipping_cost_foreign) < 0
    ) {
      setError("נא להזין עלות משלוח תקינה");
      return false;
    }

    if (!formData.vehicle_weight_category) {
      setError("נא לבחור משקל רכב");
      return false;
    }

    // בדיקות לפי מסלול
    // const weightCategory = formData.vehicle_weight_category;

    return true;
  };

  // ========================================
  // API Handlers
  // ========================================

  // שלב 1: חישוב בסיסי
  const handleStep1Submit = async () => {
    if (!termsAccepted) {
      setError("יש לאשר את תנאי השימוש ומדיניות הפרטיות לפני ביצוע חישוב");
      return;
    }
    setError("");

    if (!validateStep1()) {
      return;
    }

    setLoading(true);

    try {
      const requestData: any = {
        manufacturing_date: formData.manufacturing_date,
        vehicle_price_foreign: parseFloat(formData.vehicle_price_foreign),
        currency_type: formData.currency_type,
        exchange_rate: parseFloat(formData.exchange_rate),
        shipping_cost_foreign: parseFloat(formData.shipping_cost_foreign) || 0,
        insurance_cost_foreign:
          parseFloat(formData.insurance_cost_foreign) || 0,
        insurance_currency_type:
          formData.insurance_currency_type || formData.currency_type,
        insurance_exchange_rate:
          parseFloat(formData.insurance_exchange_rate) ||
          parseFloat(formData.exchange_rate),
        local_expenses_ils: parseFloat(formData.local_expenses_ils) || 0,
        has_origin_declaration: formData.has_origin_declaration,
        vehicle_weight_category: formData.vehicle_weight_category,
      };

      // הוספת מספר כריות למסלול 2
      if (formData.vehicle_weight_category === "between_3500_4500") {
        requestData.airbags_count = parseInt(formData.airbags_count) || 0;
      }

      // הוספת אבזור בטיחותי למסלול 3
      if (formData.vehicle_weight_category === "under_3500") {
        requestData.safety_features = safetyInputMode === "select"
          ? SAFETY_LEVEL_TO_FEATURES[selectedSafetyLevel] as SafetyEquipmentData
          : safetyEquipmentData;
      }

      const response = await calculateN1N2ElectricCommercial(requestData);

      if (response.res === "OK" && response.data) {
        setBasicCalculationResult(response.data);

        // בדיקה אם צריך שלב 2
        if (response.data.calculation_info?.needs_step2) {
          setCurrentStep(2); // עבור לשלב 2
        } else {
          setFinalCalculationResult(response.data);
          setCurrentStep(3); // ישר לתוצאות
        }
      } else {
        setError(response.msg || "שגיאה בחישוב");
      }
    } catch (error) {
      console.error("שגיאה בחישוב:", error);
      setError("שגיאה בתקשורת עם השרת");
    } finally {
      setLoading(false);
    }
  };

  // שלב 2: חישוב סופי עם מחיר לצרכן (רק מסלול 3)
  const handleStep2Submit = async () => {
    setError("");

    if (!consumerPrice || parseFloat(consumerPrice) <= 0) {
      setError("נא להזין מחיר לצרכן תקין");
      return;
    }

    setLoading(true);

    try {
      const requestData: any = {
        manufacturing_date: formData.manufacturing_date,
        vehicle_price_foreign: parseFloat(formData.vehicle_price_foreign),
        currency_type: formData.currency_type,
        exchange_rate: parseFloat(formData.exchange_rate),
        shipping_cost_foreign: parseFloat(formData.shipping_cost_foreign) || 0,
        insurance_cost_foreign:
          parseFloat(formData.insurance_cost_foreign) || 0,
        insurance_currency_type:
          formData.insurance_currency_type || formData.currency_type,
        insurance_exchange_rate:
          parseFloat(formData.insurance_exchange_rate) ||
          parseFloat(formData.exchange_rate),
        local_expenses_ils: parseFloat(formData.local_expenses_ils) || 0,
        has_origin_declaration: formData.has_origin_declaration,
        vehicle_weight_category: formData.vehicle_weight_category,
        safety_features: safetyInputMode === "select"
          ? SAFETY_LEVEL_TO_FEATURES[selectedSafetyLevel] as SafetyEquipmentData
          : safetyEquipmentData,
        consumer_price: parseFloat(consumerPrice),
        calculated_price_with_vat:
          basicCalculationResult?.final_results?.final_total || 0,
      };

      const response = await calculateN1N2ElectricCommercialFinal(requestData);

      if (response.res === "OK" && response.data) {
        setFinalCalculationResult(response.data);
        setCurrentStep(3); // עבור לתוצאות
      } else {
        setError(response.msg || "שגיאה בחישוב סופי");
      }
    } catch (error) {
      console.error("שגיאה בחישוב סופי:", error);
      setError("שגיאה בתקשורת עם השרת");
    } finally {
      setLoading(false);
    }
  };

  // שמירה
  const handleSaveCalculation = async () => {
    if (!finalCalculationResult) return;

    setIsSaving(true);
    try {
      const saveRequest = {
        calculation_type: "N1_N2_electric_commercial",
        input_data: finalCalculationResult.input_data,
        result_data: {
          ...finalCalculationResult,
          commercial_actual_cost_data: commercialActualCostData,
        },
        notes: userNotes.trim(),
      };

      const response = await saveCalculationWithNotes(saveRequest);

      if (response.res === "OK") {
        setIsSaved(true);
        setTimeout(() => navigate("/enduser/dashboard"), 1500);
      } else {
        setError(response.msg || "שגיאה בשמירת החישוב");
      }
    } catch (error) {
      console.error("שגיאה בשמירה:", error);
      setError("שגיאה בשמירת החישוב");
    } finally {
      setIsSaving(false);
    }
  };

  // חזרה לעריכה
  const handleGoBack = () => {
    if (currentStep === 3) {
      // מתוצאות חוזרים לשלב המתאים
      if (needsStep2) {
        setCurrentStep(2);
      } else {
        setCurrentStep(1);
      }
      setFinalCalculationResult(null);
    } else if (currentStep === 2) {
      setCurrentStep(1);
      setConsumerPrice("");
      setBasicCalculationResult(null);
    } else {
      navigate("/enduser/dashboard");
    }
  };

  // הדפסה — מסמך נקי דרך המודול (דסקטופ: iframe מבודד, מובייל: טאב חדש)
  const handlePrint = () => {
    printCalculationDocument(printRef.current, {
      calculatorName: "מחשבון N1 & N2 חשמלי - ייבוא מסחרי",
      calculationDate: new Date().toLocaleDateString("he-IL"),
      variant: "calculator",
    });
  };

  // ========================================
  // JSX Rendering
  // ========================================

  return (
    <div
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="mx-auto mb-4 text-6xl"></div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-3">
            <span>מחשבון N1 & N2 חשמלי - ייבוא מסחרי</span>
          </h1>
          <p className="text-gray-600">
            חישוב מדויק של מיסי ייבוא מסחרי לרכב משא חשמלי N1 & N2
          </p>
        </div>

        {/* Error Display */}
        {/* {error && (
          <MotionCard className="mb-6 bg-red-50 border-2 border-red-200">
            <div className="flex items-start gap-3">
              <span className="text-2xl">❌</span>
              <div className="flex-1">
                <h3 className="font-bold text-red-800 mb-1">שגיאה</h3>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          </MotionCard>
        )} */}

        {/* Step 1: Main Input Form */}
        {currentStep === 1 && (
          <>
            <MotionCard className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span>📝</span>
                <span>פרטי הרכב</span>
              </h2>

              <div className="space-y-6">
                <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="terms-calculator"
                      checked={termsAccepted}
                      onChange={(e) => {
                        setTermsAccepted(e.target.checked);
                        setError("");
                      }}
                      className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor="terms-calculator"
                      className="text-sm text-gray-700 flex-1"
                    >
                      קראתי ואני מאשר/ת את{" "}
                      <Link
                        to="/terms-and-privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline font-semibold"
                      >
                        תנאי השימוש ומדיניות הפרטיות
                      </Link>{" "}
                      *
                    </label>
                  </div>
                </div>
                {/* תאריך ייצור */}
                <div>
                  <label
                    htmlFor="manufacturing_date"
                    className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
                  >
                    תאריך ייצור הרכב <span className="text-red-500">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.manufacturing_date}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="manufacturing_date"
                    type="date"
                    aria-required="true"
                    value={formData.manufacturing_date}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        manufacturing_date: e.target.value,
                      }))
                    }
                    max={new Date().toISOString().split("T")[0]}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-orange-800 mt-1 font-medium">
                    ⚠️ יש לעקוב אחר הנחיות משרד התחבורה המתעדכנות מעת לעת
                  </p>
                </div>

                {/* מחיר הרכב */}
                <div>
                  <label
                    htmlFor="vehicle_price_foreign"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    מחיר הרכב במטבע חוץ <span className="text-red-500">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.vehicle_price}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="vehicle_price_foreign"
                    type="number"
                    aria-required="true"
                    value={formData.vehicle_price_foreign}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "vehicle_price_foreign",
                        e.target.value
                      )
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* בחירת מטבע */}
                <div>
                  <label
                    htmlFor="currency_type"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    סוג מטבע <span className="text-red-500">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.currency_selection}
                      explanations={explanations}
                    />
                  </label>
                  <select
                    id="currency_type"
                    aria-required="true"
                    value={formData.currency_type}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        currency_type: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {currencyRates.map((rate) => (
                      <option
                        key={rate.currency_code}
                        value={rate.currency_code}
                      >
                        {rate.currency_name_hebrew} ({rate.currency_code})
                      </option>
                    ))}
                  </select>
                </div>

                {/* שער חליפין */}
                <div>
                  <label
                    htmlFor="exchange_rate"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    שער חליפין (מכס) <span className="text-red-500">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.exchange_rate}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="exchange_rate"
                    type="number"
                    aria-required="true"
                    value={formData.exchange_rate}
                    onChange={(e) =>
                      handleNumberInputChange("exchange_rate", e.target.value)
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.0000"
                    min="0"
                    step="0.0001"
                  />
                </div>

                {/* עלות משלוח */}
                <div>
                  <label
                    htmlFor="shipping_cost_foreign"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    עלות משלוח במטבע חוץ <span className="text-red-500">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.shipping_cost}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="shipping_cost_foreign"
                    type="number"
                    aria-required="true"
                    value={formData.shipping_cost_foreign}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "shipping_cost_foreign",
                        e.target.value
                      )
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* עלות ביטוח */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    עלות ביטוח במטבע חוץ
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.insurance_cost}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    type="number"
                    value={formData.insurance_cost_foreign}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "insurance_cost_foreign",
                        e.target.value
                      )
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* מטבע ביטוח */}
                <div>
                  <label
                    htmlFor="insurance_currency_type"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    סוג מטבע ביטוח
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.insurance_currency}
                      explanations={explanations}
                    />
                  </label>
                  <select
                    id="insurance_currency_type"
                    value={formData.insurance_currency_type}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        insurance_currency_type: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {currencyRates.map((rate) => (
                      <option
                        key={rate.currency_code}
                        value={rate.currency_code}
                      >
                        {rate.currency_name_hebrew} ({rate.currency_code})
                      </option>
                    ))}
                  </select>
                </div>

                {/* שער חליפין ביטוח */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    שער חליפין ביטוח (מכס)
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.insurance_exchange_rate}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    type="number"
                    value={formData.insurance_exchange_rate}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "insurance_exchange_rate",
                        e.target.value
                      )
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.0000"
                    min="0"
                    step="0.0001"
                  />
                </div>

                {/* הוצאות מקומיות */}
                <div>
                  <label
                    htmlFor="local_expenses_ils"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    הוצאות מקומיות (₪) <span className="text-red-500">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.local_expenses}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="local_expenses_ils"
                    type="number"
                    aria-required="true"
                    value={formData.local_expenses_ils}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "local_expenses_ils",
                        e.target.value
                      )
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* הצהרת מקור */}
                <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.has_origin_declaration}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          has_origin_declaration: e.target.checked,
                        }))
                      }
                      className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">
                        יש הצהרת מקור
                        <ExplanationTooltip
                          stepKey={STEP_KEYS.origin_declaration}
                          explanations={explanations}
                        />
                      </span>
                      <p className="text-sm text-gray-600 mt-1">
                        הצהרת מקור פוטרת ממכס (7%)
                      </p>
                    </div>
                  </label>
                </div>

                {/* משקל הרכב */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    משקל הרכב *
                    {/* <ExplanationTooltip
                      stepKey={STEP_KEYS.vehicle_weight}
                      explanations={explanations}
                    /> */}
                  </label>
                  <div className="space-y-3">
                    {/* מתחת ל-3.5 טון */}
                    <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                      <input
                        type="radio"
                        name="weight_category"
                        value="under_3500"
                        checked={
                          formData.vehicle_weight_category === "under_3500"
                        }
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            vehicle_weight_category: e.target.value as any,
                            airbags_count: "",
                          }));
                          // איפוס אבזור בטיחותי
                          setSafetyEquipmentData({
                            airbagsCount: 0,
                            systems: {
                              blindSpotDetection: false,
                              blindSpotCollisionPrevention: false,
                              reverseBraking: false,
                              driverDistraction: false,
                              childAlert: false,
                              doorOpenWarning: false,
                              adaptiveLighting: false,
                              junctionEmergencyBraking: false,
                            },
                          });
                        }}
                        className="mt-1 w-4 h-4 text-blue-600"
                      />
                      <div className="mr-3">
                        <div className="font-bold text-gray-900">
                          מתחת ל-3.5 טון
                        </div>
                      </div>
                    </label>

                    {/* בין 3.5-4.5 טון */}
                    <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                      <input
                        type="radio"
                        name="weight_category"
                        value="between_3500_4500"
                        checked={
                          formData.vehicle_weight_category ===
                          "between_3500_4500"
                        }
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            vehicle_weight_category: e.target.value as any,
                            airbags_count: "",
                          }));
                          // איפוס אבזור בטיחותי
                          setSafetyEquipmentData({
                            airbagsCount: 0,
                            systems: {
                              blindSpotDetection: false,
                              blindSpotCollisionPrevention: false,
                              reverseBraking: false,
                              driverDistraction: false,
                              childAlert: false,
                              doorOpenWarning: false,
                              adaptiveLighting: false,
                              junctionEmergencyBraking: false,
                            },
                          });
                        }}
                        className="mt-1 w-4 h-4 text-blue-600"
                      />
                      <div className="mr-3">
                        <div className="font-bold text-gray-900">
                          בין 3.5 ל-4.5 טון
                        </div>
                      </div>
                    </label>

                    {/* מעל 4.5 טון */}
                    <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                      <input
                        type="radio"
                        name="weight_category"
                        value="over_4500"
                        checked={
                          formData.vehicle_weight_category === "over_4500"
                        }
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            vehicle_weight_category: e.target.value as any,
                            airbags_count: "",
                          }));
                          // איפוס אבזור בטיחותי
                          setSafetyEquipmentData({
                            airbagsCount: 0,
                            systems: {
                              blindSpotDetection: false,
                              blindSpotCollisionPrevention: false,
                              reverseBraking: false,
                              driverDistraction: false,
                              childAlert: false,
                              doorOpenWarning: false,
                              adaptiveLighting: false,
                              junctionEmergencyBraking: false,
                            },
                          });
                        }}
                        className="mt-1 w-4 h-4 text-blue-600"
                      />
                      <div className="mr-3">
                        <div className="font-bold text-gray-900">
                          מעל 4.5 טון
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </MotionCard>

            {/* מסלול 2: כריות אוויר (3.5-4.5 טון) */}
            {formData.vehicle_weight_category === "between_3500_4500" && (
              <MotionCard className="mb-6 bg-yellow-50 border-2 border-yellow-300">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                 
                  <span>מספר כריות אוויר</span>
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      מספר כריות אוויר *
                    </label>
                    <input
                      type="number"
                      value={formData.airbags_count}
                      onChange={(e) =>
                        handleNumberInputChange("airbags_count", e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                      min="0"
                    />
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-yellow-200">
                    <p className="text-sm text-gray-700 font-medium mb-2">
                      💡 הנחה על כריות אוויר:
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 4 כריות: הנחה של 400 ₪</li>
                      <li>• מעל 4 כריות: הנחה של 800 ₪</li>
                    </ul>
                  </div>
                </div>
              </MotionCard>
            )}

            {/* אבזור בטיחותי - רק למתחת ל-3.5 טון */}
            {formData.vehicle_weight_category === "under_3500" && (
              <MotionCard className="p-6 mb-6 bg-orange-50 border-2 border-orange-300">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  🛡️ אבזור בטיחותי N1 & N2
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  הנחה אפשרית עד 2,400 ש"ח לפי רמת אבזור. ההנחה תשוקלל במחיר הסופי.
                </p>

                {/* מצב בחירה מהירה */}
                {safetyInputMode === "select" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      בחר רמת אבזור בטיחותי (0–8)
                    </label>
                    <select
                      value={selectedSafetyLevel}
                      onChange={(e) => setSelectedSafetyLevel(parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent mb-3"
                    >
                      <option value="0">רמה 0 — ללא הנחה (0 ₪)</option>
                      <option value="1">רמה 1 — הנחה 250 ₪</option>
                      <option value="2">רמה 2 — הנחה 600 ₪</option>
                      <option value="3">רמה 3 — הנחה 1,000 ₪</option>
                      <option value="4">רמה 4 — הנחה 1,600 ₪</option>
                      <option value="5">רמה 5 — הנחה 1,850 ₪</option>
                      <option value="6">רמה 6 — הנחה 2,050 ₪</option>
                      <option value="7">רמה 7 — הנחה 2,250 ₪</option>
                      <option value="8">רמה 8 — הנחה 2,400 ₪</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => setSafetyInputMode("calculator")}
                      className="text-sm text-orange-600 underline hover:text-orange-800"
                    >
                      לא יודע את הרמה? פתח מחשבון אבזור בטיחותי
                    </button>
                  </div>
                )}

                {/* מצב מחשבון מפורט */}
                {safetyInputMode === "calculator" && (
                  <div>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        מספר כריות אוויר
                      </label>
                      <input
                        type="number" min="0" max="20"
                        value={safetyEquipmentData.airbagsCount}
                        onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, airbagsCount: parseInt(e.target.value) || 0 }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="הזן מספר כריות"
                      />
                      {safetyEquipmentData.airbagsCount < 4 && safetyEquipmentData.airbagsCount > 0 && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                          ⚠️ דרושות לפחות 4 כריות אוויר לקבלת הנחה
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <h4 className="font-bold text-gray-900 mb-3">
                        מערכות בטיחות (ציון כולל: {safetyScore.toFixed(1)})
                      </h4>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.blindSpotDetection}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, blindSpotDetection: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת זיהוי כלי רכב ב"שטח מת"</div><div className="text-xs text-gray-500">ציון: 1.0</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.blindSpotCollisionPrevention}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, blindSpotCollisionPrevention: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת אקטיבית למניעת התנגשות צד ב"שטח מת"</div><div className="text-xs text-gray-500">ציון: 2.0</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.reverseBraking}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, reverseBraking: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">בלימה אוטומטית בנסיעה לאחור</div><div className="text-xs text-gray-500">ציון: 1.0</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.driverDistraction}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, driverDistraction: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת התרעה במצב של היסח דעת הנהג</div><div className="text-xs text-gray-500">ציון: 1.0</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.childAlert}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, childAlert: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת התרעה על השארת ילדים ברכב</div><div className="text-xs text-gray-500">ציון: 1.5</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.doorOpenWarning}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, doorOpenWarning: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת אזהרת פתיחת דלת</div><div className="text-xs text-gray-500">ציון: 1.5</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.adaptiveLighting}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, adaptiveLighting: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת תאורה אדפטיבית / שליטה באורות גבוהים</div><div className="text-xs text-gray-500">ציון: 1.0</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.junctionEmergencyBraking}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, junctionEmergencyBraking: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת בלימת חירום לתנועה חוצה בצמתים</div><div className="text-xs text-gray-500">ציון: 1.5</div></div>
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSafetyInputMode("select")}
                      className="mt-4 text-sm text-orange-600 underline hover:text-orange-800"
                    >
                      ← חזור לבחירה מהירה
                    </button>
                  </div>
                )}

                {/* תיבת סיכום — מוצגת כשרמה > 0 */}
                {safetyLevel > 0 && (
                  <div className="mt-4 p-3 bg-orange-100 border border-orange-300 rounded-lg text-sm text-orange-800 font-medium">
                    רמת אבזור: {safetyLevel} | הנחה צפויה: {safetyDiscount.toLocaleString()} ₪
                  </div>
                )}
              </MotionCard>
            )}
            {/* הודעת שגיאה */}
            {error && (
              <MotionCard className="mb-6 bg-red-50 border-2 border-red-200">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">❌</span>
                  <div className="flex-1">
                    <h3 className="font-bold text-red-800 mb-1">שגיאה</h3>
                    <p className="text-red-700">{error}</p>
                  </div>
                </div>
              </MotionCard>
            )}

            {/* כפתור חישוב */}
            <div className="flex gap-4"></div>
            {/* כפתור חישוב */}
            <div className="flex gap-4">
              <MotionButton
                onClick={() => navigate("/enduser/dashboard")}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-bold"
              >
                ← ביטול
              </MotionButton>

              <MotionButton
                onClick={handleStep1Submit}
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? "⏳ מחשב..." : "🧮 חשב מיסים →"}
              </MotionButton>
            </div>
          </>
        )}
        {/* Step 2: מחיר לצרכן (רק מסלול 3) */}
        {currentStep === 2 && (
          <>
            <MotionCard className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span>שלב 2: מחיר לצרכן</span>
              </h2>

              <div className="space-y-6">
                {/* הצגת המחיר המחושב */}
                <div className="bg-white rounded-lg p-4 border-2 border-blue-300">
                  <h3 className="font-bold text-gray-900 mb-2">
                     עלות הרכב עד כה - (כולל מע"מ):
                  </h3>
                  <p className="text-3xl font-bold text-blue-600">
                    {basicCalculationResult?.final_results?.final_total?.toLocaleString() ||
                      "0"}{" "}
                    ₪
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    אנא הזן מחיר לצרכן
                  </p>
                </div>

                {/* הזנת מחיר לצרכן */}
                <div>
                  <label
                    htmlFor="consumer_price"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    מחיר לצרכן (₪) *
                  </label>
                  <input
                    id="consumer_price"
                    type="text"
                    inputMode="numeric"
                    aria-required="true"
                    value={formatThousands(consumerPrice)}
                    onChange={(e) =>
                      setConsumerPrice(toRawNumber(e.target.value))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="הזן מחיר לצרכן"
                  />
                </div>
              </div>
            </MotionCard>

            {/* כפתורים */}
            <div className="flex gap-4">
              <MotionButton
                onClick={() => {
                  setCurrentStep(1);
                  setConsumerPrice("");
                }}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-bold"
              >
                ← חזור
              </MotionButton>

              <MotionButton
                onClick={handleStep2Submit}
                disabled={loading || !consumerPrice}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? "⏳ מחשב..." : "🧮 חישוב סופי →"}
              </MotionButton>
            </div>
          </>
        )}

        {/* Step 3: תוצאות */}
        {currentStep === 3 && finalCalculationResult && (
          <div className="space-y-6">
            {/* אזור התוצאה הניתן להדפסה (לא כולל "דייק לי" וכרטיס השמירה) */}
            <div ref={printRef} className="space-y-6">
            <TaxYearNotice />
            {/* סיכום נתוני קלט */}
            <MotionCard className="p-6 bg-blue-50 border-2 border-blue-300">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                📋 סיכום נתוני קלט
              </h2>

              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between py-2 border-b border-blue-200">
                  <span className="font-medium">מחיר רכב:</span>
                  <span className="font-bold">
                    {finalCalculationResult.input_data?.vehicle_price_foreign?.toLocaleString()}{" "}
                    {finalCalculationResult.input_data?.currency_type}
                  </span>
                </div>

                <div className="flex justify-between py-2 border-b border-blue-200">
                  <span className="font-medium">שער המרה:</span>
                  <span className="font-bold">
                    {finalCalculationResult.input_data?.exchange_rate?.toFixed(
                      4
                    )}
                  </span>
                </div>

                <div className="flex justify-between py-2 border-b border-blue-200">
                  <span className="font-medium">עלות משלוח:</span>
                  <span className="font-bold">
                    {finalCalculationResult.input_data?.shipping_cost_foreign?.toLocaleString()}{" "}
                    {finalCalculationResult.input_data?.currency_type}
                  </span>
                </div>
                {/* הוצאות מקומיות בש"ח - שדה חדש */}
                <div className="flex justify-between py-2 border-b border-blue-200">
                  <span className="font-medium">הוצאות מקומיות:</span>
                  <span className="font-bold">
                    {finalCalculationResult.input_data?.local_expenses_ils?.toLocaleString() ||
                      0}{" "}
                    ₪
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-blue-200">
                  <span className="font-medium">קטגוריית משקל:</span>
                  <span className="font-bold">
                    {finalCalculationResult.input_data
                      ?.vehicle_weight_category === "under_3500" &&
                      "מתחת ל-3.5 טון"}
                    {finalCalculationResult.input_data
                      ?.vehicle_weight_category === "between_3500_4500" &&
                      "בין 3.5-4.5 טון"}
                    {finalCalculationResult.input_data
                      ?.vehicle_weight_category === "over_4500" &&
                      "מעל 4.5 טון"}
                  </span>
                </div>

                <div className="flex justify-between py-2 border-b border-blue-200">
                  <span className="font-medium">הצהרת מקור:</span>
                  <span className="font-bold">
                    {finalCalculationResult.input_data?.has_origin_declaration
                      ? "כן ✓"
                      : "לא"}
                  </span>
                </div>

                {/* מחיר לצרכן - רק במסלול 3 */}
                {finalCalculationResult.input_data?.consumer_price && (
                  <div className="flex justify-between py-2 border-b border-blue-200">
                    <span className="font-medium">מחיר לצרכן:</span>
                    <span className="font-bold">
                      {finalCalculationResult.input_data.consumer_price.toLocaleString()}{" "}
                      ₪
                    </span>
                  </div>
                )}
              </div>
            </MotionCard>

            {/* תוצאות החישוב */}
            <MotionCard className="p-6 bg-green-50 border-2 border-green-300">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                 תוצאות החישוב
                {finalCalculationResult.input_data?.vehicle_weight_category ===
                  "under_3500" &&
                  finalCalculationResult.input_data?.consumer_price && (
                    <span className="text-sm font-normal text-gray-600">
                      (מחיר לצרכן:{" "}
                      {finalCalculationResult.input_data.consumer_price.toLocaleString()}{" "}
                      ₪)
                    </span>
                  )}
                {finalCalculationResult.input_data?.vehicle_weight_category ===
                  "over_4500" && (
                  <span className="text-sm font-normal text-gray-600">
                    (רכב מעל 4.5 טון - פטור ממס קניה)
                  </span>
                )}
                {finalCalculationResult.input_data?.vehicle_weight_category ===
                  "between_3500_4500" && (
                  <span className="text-sm font-normal text-gray-600">
                    (רכב 3.5-4.5 טון - מס קניה 72%)
                  </span>
                )}
              </h2>

              <div className="space-y-2 text-sm">
                {/* 1. ערך למכס */}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">1. ערך למכס (₪):</span>
                  <span className="font-bold">
                    {(
                      finalCalculationResult.intermediate_calculations
                        ?.total_value_for_customs || 0
                    ).toLocaleString()}{" "}
                    ₪
                  </span>
                </div>

                {/* 2. מכס */}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">2. מכס:</span>
                  <span className="font-bold">
                    {finalCalculationResult.intermediate_calculations
                      ?.customs_exemption_reason ? (
                      <span className="text-green-600">
                        פטור -{" "}
                        {
                          finalCalculationResult.intermediate_calculations
                            .customs_exemption_reason
                        }
                      </span>
                    ) : (
                      <>
                        {(
                          finalCalculationResult.intermediate_calculations
                            ?.customs_fee || 0
                        ).toLocaleString()}{" "}
                        ₪
                      </>
                    )}
                  </span>
                </div>

                {/* 3. שיעור מכס */}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">3. שיעור מכס:</span>
                  <span className="font-bold">
                    {finalCalculationResult.intermediate_calculations
                      ?.customs_rate_percent || 0}
                    %
                  </span>
                </div>

                {/* 4. מס קניה בסיסי - רק אם יש */}
                {finalCalculationResult.intermediate_calculations
                  ?.base_purchase_tax !== undefined &&
                  finalCalculationResult.intermediate_calculations
                    ?.base_purchase_tax > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="font-medium">
                        4. מס קניה בסיסי (
                        {finalCalculationResult.intermediate_calculations
                          ?.base_purchase_tax_rate_percent || 0}
                        %):
                      </span>
                      <span className="font-bold">
                        {(
                          finalCalculationResult.intermediate_calculations
                            ?.base_purchase_tax || 0
                        ).toLocaleString()}{" "}
                        ₪
                      </span>
                    </div>
                  )}

                {/* ציון ירוק - רק אם יש וראופציה 1 נבחרה */}
                {finalCalculationResult.intermediate_calculations
                  ?.green_rating_info &&
                  finalCalculationResult.intermediate_calculations
                    ?.selected_option === 1 && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="font-medium">
                        5. ציון ירוק -{" "}
                        {
                          finalCalculationResult.intermediate_calculations
                            .green_rating_info.group_display_name
                        }
                        :
                      </span>
                      <span className="font-bold">
                        {finalCalculationResult.intermediate_calculations
                          .green_rating_info.raw_amount < 0
                          ? "-"
                          : "+"}
                        {Math.abs(
                          finalCalculationResult.intermediate_calculations
                            .green_rating_info.raw_amount || 0
                        ).toLocaleString()}{" "}
                        ₪
                      </span>
                    </div>
                  )}

                {/* אבזור בטיחותי - מוצג תמיד אם יש level (גם אם 0) */}
                {finalCalculationResult.intermediate_calculations
                  ?.safety_level !== null &&
                  finalCalculationResult.intermediate_calculations
                    ?.safety_level !== undefined && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="font-medium">
                        6. אבזור בטיחותי (רמה{" "}
                        {
                          finalCalculationResult.intermediate_calculations
                            .safety_level
                        }
                        ):
                      </span>
                      <span className="font-bold">
                        {finalCalculationResult.intermediate_calculations
                          .safety_discount > 0 ? (
                          <>
                            -
                            {(
                              finalCalculationResult.intermediate_calculations
                                .safety_discount || 0
                            ).toLocaleString()}{" "}
                            ₪
                          </>
                        ) : (
                          <>0 ₪ (לא הופעל)</>
                        )}
                      </span>
                    </div>
                  )}

                {/* הנחת כריות אוויר - רק למסלול 2 */}
                {finalCalculationResult.intermediate_calculations
                  ?.airbags_count !== null &&
                  finalCalculationResult.intermediate_calculations
                    ?.airbags_count !== undefined && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="font-medium">
                         כריות אוויר (
                        {
                          finalCalculationResult.intermediate_calculations
                            .airbags_count
                        }{" "}
                        כריות):
                      </span>
                      <span className="font-bold">
                        {finalCalculationResult.intermediate_calculations
                          .airbags_discount > 0 ? (
                          <>
                            -
                            {(
                              finalCalculationResult.intermediate_calculations
                                .airbags_discount || 0
                            ).toLocaleString()}{" "}
                            ₪
                          </>
                        ) : (
                          <>0 ₪ (אין הנחה)</>
                        )}
                      </span>
                    </div>
                  )}

                {/* מס קניה אחרי הנחות - רק אם יש מס קניה */}
                {finalCalculationResult.intermediate_calculations
                  ?.purchase_tax_after_discounts !== undefined && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-medium">מס קניה אחרי הנחות:</span>
                    <span className="font-bold">
                      {(
                        finalCalculationResult.intermediate_calculations
                          .purchase_tax_after_discounts || 0
                      ).toLocaleString()}{" "}
                      ₪
                    </span>
                  </div>
                )}

                {/* מס יוקרה - רק אם יש */}
                {/* מס יוקרה - רק אם יש */}
                {finalCalculationResult.intermediate_calculations
                  ?.luxury_tax_amount > 0 && (
                  <>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="font-medium">
                        מס יוקרה חל - מחיר מעל 300,000 ש"ח
                      </span>
                      <span className="font-bold">✓</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="font-medium">7. שיעור מס יוקרה:</span>
                      <span className="font-bold">
                        {(
                          finalCalculationResult.intermediate_calculations
                            .luxury_tax_rate_percent || 0
                        ).toFixed(2)}
                        %
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="font-medium">8. סך מס יוקרה:</span>
                      <span className="font-bold">
                        {(
                          finalCalculationResult.intermediate_calculations
                            .luxury_tax_amount || 0
                        ).toLocaleString()}{" "}
                        ₪
                      </span>
                    </div>
                  </>
                )}

                {/* מס קניה סופי - רק אם יש מס קניה */}
                {finalCalculationResult.final_results?.purchase_tax_final !==
                  undefined &&
                  finalCalculationResult.final_results?.purchase_tax_final >
                    0 && (
                    <>
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="font-medium">מס קניה סופי:</span>
                        <span className="font-bold">
                          {(
                            finalCalculationResult.final_results
                              .purchase_tax_final || 0
                          ).toLocaleString()}{" "}
                          ₪
                        </span>
                      </div>

                      {/* שיעור מס קניה אפקטיבי */}
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="font-medium">
                          שיעור מס קניה אפקטיבי:
                        </span>
                        <span className="font-bold">
                          {(
                            finalCalculationResult.final_results
                              .effective_purchase_tax_rate || 0
                          ).toFixed(2)}
                          %
                        </span>
                      </div>
                    </>
                  )}

                {/* בסיס למע"מ */}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">בסיס למע"מ:</span>
                  <span className="font-bold">
                    {(
                      finalCalculationResult.final_results?.vat_base || 0
                    ).toLocaleString()}{" "}
                    ₪
                  </span>
                </div>

                {/* מע"מ */}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">סך המע"מ (18%):</span>
                  <span className="font-bold">
                    {(
                      finalCalculationResult.final_results?.vat || 0
                    ).toLocaleString()}{" "}
                    ₪
                  </span>
                </div>

                {/* סך המיסים */}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">סך המיסים:</span>
                  <span className="font-bold">
                    {(
                      finalCalculationResult.final_results?.total_taxes || 0
                    ).toLocaleString()}{" "}
                    ₪
                  </span>
                </div>

                {/* שיעור מיסוי */}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">שיעור מיסוי כולל:</span>
                  <span className="font-bold">
                    {(
                      finalCalculationResult.final_results?.tax_percentage || 0
                    ).toFixed(1)}
                    %
                  </span>
                </div>

                {/* תוצאה סופית */}
                <div className="flex justify-between items-center py-3 border-t-2 border-gray-800 px-4 mt-2">
                  <span className="font-bold text-lg">
                    עלות הרכב כולל מיסים:
                  </span>
                  <span className="font-bold text-2xl">
                    {(
                      finalCalculationResult.final_results?.final_total || 0
                    ).toLocaleString()}{" "}
                    ₪
                  </span>
                </div>
              </div>
            </MotionCard>
            {/* סוף אזור ההדפסה */}

            {/* דייק לי את התוצאה */}
            <ActualCostCalculator
              mode="commercial"
              vehicleCurrency={formData.currency_type}
              vehicleExchangeRate={formData.exchange_rate}
              vehiclePriceForeign={formData.vehicle_price_foreign}
              shippingCostForeign={formData.shipping_cost_foreign}
              insuranceCurrency={formData.insurance_currency_type}
              insuranceExchangeRate={formData.insurance_exchange_rate}
              insuranceCostForeign={formData.insurance_cost_foreign}
              localExpensesIls={formData.local_expenses_ils}
              totalTaxes={finalCalculationResult?.final_results?.total_taxes ?? 0}
              onDataChange={setCommercialActualCostData}
              explanations={explanations}
              initialData={commercialActualCostData}
            />
            </div>
            {/* שמירה והערות */}
            <MotionCard className="p-6 mb-6 print-exclude">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  הערות נוספות (אופציונלי):
                </label>
                <textarea
                  value={userNotes}
                  onChange={(e) =>
                    e.target.value.length <= 1000 &&
                    setUserNotes(e.target.value)
                  }
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="הוסף הערות על החישוב, פרטים נוספים על הרכב וכו'..."
                  disabled={isSaving || isSaved}
                />
                <div className="text-sm text-gray-500 mt-1">
                  {userNotes.length}/1000 תווים
                </div>
              </div>

              {/* הצגת שגיאות או הצלחה */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {error}
                </div>
              )}

              {isSaved && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
                  ✅ החישוב נשמר בהצלחה! מעביר לדף הבית...
                </div>
              )}

              {/* כפתורים - 3 כפתורים עם justify-between */}
              <div className="flex justify-between items-center mt-6">
                {/* כפתור שמאל: חזור לעריכה */}
                <MotionButton
                  onClick={handleGoBack}
                  disabled={isSaving || isSaved}
                  className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white"
                >
                  ← חזור לעריכה
                </MotionButton>

                {/* כפתור מרכז: הדפס */}
                <button
                  onClick={handlePrint}
                  disabled={isSaving || isSaved}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-gray-300"
                >
                  🖨️ הדפס
                </button>

                {/* כפתור ימין: שמור וחזור לדף הבית */}
                <button
                  onClick={handleSaveCalculation}
                  disabled={isSaving || isSaved}
                  className={`px-6 py-3 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                    isSaved
                      ? "bg-green-600 hover:bg-green-700 text-white"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {isSaving && "⏳ שומר..."}
                  {isSaved && "✓ נשמר - חזרה לדף הבית"}
                  {!isSaving && !isSaved && "💾 שמור וחזור לדף הבית"}
                </button>
              </div>
            </MotionCard>
          </div>
        )}

        {/* Footer Links */}
        <div className="text-center mt-8 pt-6 border-t border-gray-200">
          <div className="flex justify-center gap-6 text-sm text-gray-500">
            <Link
              to="/terms-and-privacy"
              className="hover:text-blue-600 transition-colors"
            >
              תנאי שימוש ופרטיות
            </Link>
            <span>|</span>
            <Link
              to="/accessibility-statement"
              className="hover:text-blue-600 transition-colors"
            >
              הצהרת נגישות
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default N1N2ElectricCommercialCalculatorPage;
